import { forwardRef } from "react";
import PropTypes from "prop-types";
import ReactSelect from "react-select";
import { theme } from "../../styles/theme";

const StyledSelect2 = {
	control: (base) => ({
		...base,
		minHeight: 36,
		borderRadius: 3,
		border: "1px solid #D7DDE5",
		fontFamily: theme.font.secondary,
		fontWeight: 500,
		color: theme.color.black,
	}),
	indicatorsContainer: (base) => ({
		...base,
		height: 36,
	}),
	option: (provided, state) => ({
		...provided,
		backgroundColor: state.isSelected ? "#33AF89" : "white",
		padding: 5,
		"&:hover": {
			backgroundColor: state.isFocused ? "#D6EFE7" : "#D6EFE7",
		},
	}),
	multiValue: (base) => ({
		...base,
		borderRadius: 13,
		backgroundColor: "#F7F8F9",
	}),
	multiValueRemove: (base) => ({
		...base,
		borderRadius: 13,
		"&:hover": {
			backgroundColor: "inherit",
			cursor: "pointer",
		},
	}),
	placeholder: (base) => ({
		...base,
		color: theme.color.gray2,
	}),
	menu: (provided) => ({
		...provided,
		zIndex: 9999,
	}),
};

const StyledSelect = {
	control: (base) => ({
		...base,
		minHeight: 26,
		borderRadius: 3,
		border: "1px solid #D7DDE5",
		fontFamily: theme.font.secondary,
		fontWeight: 500,
		color: theme.color.black,
	}),
	indicatorsContainer: (base) => ({
		...base,
		height: 26,
	}),
	option: (provided, state) => ({
		...provided,
		backgroundColor: state.isSelected ? "#33AF89" : "white",
		padding: 5,
		"&:hover": {
			backgroundColor: state.isFocused ? "#D6EFE7" : "#D6EFE7",
		},
	}),
	multiValue: (base) => ({
		...base,
		borderRadius: 13,
		backgroundColor: "#F7F8F9",
	}),
	multiValueRemove: (base) => ({
		...base,
		borderRadius: 13,
		"&:hover": {
			backgroundColor: "inherit",
			cursor: "pointer",
		},
	}),
	placeholder: (base) => ({
		...base,
		color: theme.color.gray2,
	}),
	menu: (provided) => ({
		...provided,
		zIndex: 9999,
	}),
};

const Select = forwardRef(
	({ 
		onChange, 
		onChangeMulti, 
		options, 
		value, 
		defaultValues = false, 
		name, 
		setMatters, 
		setUnitData, 
		setMatterId, 
		isBig = false, 
		isMulti = false,
		menuPortalTarget,
		menuPosition
	}, ref) => {
		const defaultValue = (options, value) => {
			if (!options || !Array.isArray(options) || options.length === 0) {
				return null;
			}
			
			// Handle case where value is null, undefined, or empty string
			if (value === null || value === undefined || value === "") {
				return null;
			}
			
			// Find the matching option - handle both string and number values
			const matchedOption = options.find((option) => {
				return option.value == value || String(option.value) === String(value);
			});
			
			return matchedOption || null;
		};

		const handleChange = (value) => {
			if (!value) return; // Handle null/undefined selection
			
			onChange && onChange(name, value.value); // Don't force string conversion
			onChangeMulti && onChangeMulti(value);
			setMatters && setMatters(value.matters);
			setMatters && onChange("matter", null);
			setUnitData && setUnitData(value);
			setMatterId && setMatterId(value.value);
		};

		return (
			<ReactSelect
				ref={ref}
				onChange={handleChange}
				value={defaultValue(options, value)}
				options={options}
				styles={!isBig ? StyledSelect : StyledSelect2}
				isMulti={isMulti}
				defaultValue={defaultValues ? defaultValues : false}
				menuPortalTarget={menuPortalTarget}
				menuPosition={menuPosition}
			/>
		);
	}
);

export default Select;

Select.displayName = "Select";

Select.propTypes = {
	options: PropTypes.array,
	defaultValues: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
	setMatters: PropTypes.func,
	onChange: PropTypes.func,
	onChangeMulti: PropTypes.func,
	setUnitData: PropTypes.func,
	setMatterId: PropTypes.func,
	value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array, PropTypes.any]),
	name: PropTypes.string,
	isBig: PropTypes.bool,
	isMulti: PropTypes.bool,
	menuPortalTarget: PropTypes.object,
	menuPosition: PropTypes.string,
};
